import ChartDataLabels from "chartjs-plugin-datalabels";
import { useMemo, useState } from "react";
import { Pie } from "react-chartjs-2";

import type { DataType } from "../../utils";
import type { PieChartData } from "../utils";
import { externalTooltipHandler, insidePieLabelsTransform, legendLabelsTransform } from "./utils";

/**
 * Pie chart and its config
 */
interface IPieChart {
    /**
     * Data for pie chart
     */
    data: PieChartData;
    /**
     * Type of data
     */
    dataType: DataType;
}
const PieChart = ({ data, dataType }: IPieChart) => {
    const [labelsInChart, setLabelsInChart] = useState<string[]>([]);

    // Get outside Pie legend labels, recalculate when labelsInChart or data value is changing
    const legendLabels = useMemo(() => {
        return (data.labels as string[] | undefined)?.filter?.((label: string) => !labelsInChart.includes(label)) || [];
    }, [labelsInChart, data]);

    const options = useMemo(
        () => ({
            responsive: true,
            plugins: {
                legend: {
                    onClick: (e) => {}, // disable on clickable legend
                    position: "bottom" as const,
                    align: "start" as const,
                    labels: {
                        usePointStyle: true,
                        pointStyleWidth: 18,
                        boxHeight: 5,
                        font: {
                            size: 10,
                        },
                        generateLabels: (chart) => legendLabelsTransform(chart, legendLabels),
                    },
                },
                tooltip: {
                    enabled: false,
                    external: (context) => externalTooltipHandler(context, dataType),
                },
                datalabels: {
                    formatter: (value: number, context) =>
                        insidePieLabelsTransform(value, context, data, setLabelsInChart), // Show data label inside the PIE
                    clip: true,
                    font: {
                        size: 10,
                    },
                },
            },
        }),
        [legendLabels, data, dataType],
    );

    return <Pie data={data} options={options as any} plugins={[ChartDataLabels as any]} />;
};

export default PieChart;
